import { IssueType } from '@finance-lab/utils/types'
import { IssueEntity } from '../types/IssueEntity'
import ApiBase from './Api'
import { getIssueTotalUnitSize, getInvestorAmountInCash, getInvestorAmountInPercentage } from './utils'
import { toSnakeCase } from '../packages/utils/casing'

const INVESTOR_API_PATHS = {
    ALL_INVESTORS: '/investors',
    INVESTOR_DETAILS: (id: string) => `/investors/${id}`,
}

const ADMIN_INVESTOR_API_PATHS = {
    ALL_INVESTORS: '/admin/investors',
    UPDATE_INVESTOR: (id: string) => `/admin/investors/${id}`,
    DELETE_INVESTOR: (id: string) => `/admin/investors/${id}`,
}

export interface InvestorDetails {
    id: string
    isInWatchList: 0 | 1 | null
    name: string
    aliases: Array<{
        id: string
        nameAlias: string
        investorId: string
    }>
    participations: Array<{
        commitment: {
            amountInCash: string | null
            amountInPercentage: string | null
            feeLevel: number | null
            id: string
            isSeller: 0 | 1 | null
            leo: 0 | 1 | null
        }
        issue: {
            cancelled: 0 | 1 | null
            currency: string | null
            financialAdvisors: Array<{ name: string }> | null
            id: string
            instrumentName: string
            publicationDate: string | null
            sector1: string | null
            sector2: string | null
            sector3: string | null
            shareId: string
            sources: IssueEntity['sources']
            subEndDate: string | null
            type: IssueType
        } | null
        warrant: {
            cancelled: 0 | 1 | null
            currency: string | null
            financialAdvisors: null
            id: string
            instrumentName: string
            sector1: string | null
            sector2: string | null
            sector3: string | null
            subEndDate: string | null
            companyName: string | null
            name: string | null
        } | null
        convertible: {
            currency: string | null
            financialAdvisors: null
            id: string
            instrumentName: string
            loanAmount: number | null
            convertibleName: string | null
            sector1: string | null
            sector2: string | null
            sector3: string | null
            maturityDate: string | null
            publicationDate: string | null
            companyName: string | null
        } | null
    }>
}

class InvestorApi extends ApiBase {
    async allInvestors() {
        return this._getRequest(ADMIN_INVESTOR_API_PATHS.ALL_INVESTORS)
    }

    async connectAsInvestorAlias(aliasId, investorId) {
        return this._patchRequest(ADMIN_INVESTOR_API_PATHS.UPDATE_INVESTOR(investorId), toSnakeCase({ aliasId }))
    }

    async setAliasAsToplevelInvestor(aliasId, oldInvestorId) {
        return this._patchRequest(
            ADMIN_INVESTOR_API_PATHS.UPDATE_INVESTOR(oldInvestorId),
            toSnakeCase({ promote: aliasId }),
        )
    }

    async setInvestorPrimaryNameFromAlias(investorId, aliasId) {
        return this._patchRequest(
            ADMIN_INVESTOR_API_PATHS.UPDATE_INVESTOR(investorId),
            toSnakeCase({ primary: aliasId }),
        )
    }

    async deleteInvestorAndAliases(investorId) {
        return this._deleteRequest(ADMIN_INVESTOR_API_PATHS.DELETE_INVESTOR(investorId))
    }

    async getInvestor(id: string) {
        const investor = await this._getRequest(INVESTOR_API_PATHS.INVESTOR_DETAILS(id))

        return {
            ...investor,
            participations: investor?.participations.map((participation) => {
                if (participation.warrant) return participation

                const totalUnitSize = participation.issue
                    ? getIssueTotalUnitSize(participation.issue)
                    : participation.convertible.loanAmount

                return {
                    ...participation,
                    commitment: {
                        ...participation.commitment,
                        amountInCash: getInvestorAmountInCash(participation.commitment, totalUnitSize),
                        amountInPercentage: getInvestorAmountInPercentage(participation.commitment, totalUnitSize),
                    },
                }
            }),
        } as InvestorDetails
    }
}

export default InvestorApi
